import { API_BASE_URL, getBaseHeaders } from './api.config';
import { ApiError, BackendErrorResponse, parseApiErrorResponse, throwApiError } from './api.error';
import { Capacitor } from '@capacitor/core';
import { KeychainAccess, SecureStorage } from '@aparajita/capacitor-secure-storage';
import { forgetGoogleSession } from './google-auth';
import type { UserAccountType } from './api.types';
import { trackEvent } from './analytics';

export { ApiError, DEFAULT_API_ERROR_MESSAGE } from './api.error';

const LEGACY_AUTH_STORAGE_KEYS = [
  'roade_access_token',
  'roade_refresh_token',
  'roade_user_id',
  'roade_user_role',
  'isLoggedIn',
];
const NATIVE_REFRESH_TOKEN_KEY = 'refresh_token';
const NATIVE_REFRESH_TOKEN_HEADER = 'ROADE-NATIVE-REFRESH-TOKEN';
const CONCURRENT_REFRESH_RETRY_DELAY_MS = 150;
const AUTH_SESSION_HINT_KEY = 'roade_auth_session_hint';
const ACCESS_TOKEN_REFRESH_SKEW_MS = 30_000;
const isNative = Capacitor.isNativePlatform();

let accessToken: string | null = null;
let currentUserId: string | null = null;
let currentUserRole: string | null = null;
let currentUserAccountType: UserAccountType | null = null;
let refreshRequest: Promise<RefreshTokenResponse | null> | null = null;
let secureStorageReady: Promise<void> | null = null;

const prepareSecureStorage = async (): Promise<void> => {
  if (!isNative) return;
  if (!secureStorageReady) {
    secureStorageReady = Promise.all([
      SecureStorage.setKeyPrefix('roade_'),
      SecureStorage.setDefaultKeychainAccess(KeychainAccess.whenUnlockedThisDeviceOnly),
    ]).then(() => undefined);
  }
  await secureStorageReady;
};

const getNativeRefreshToken = async (): Promise<string | null> => {
  await prepareSecureStorage();
  return SecureStorage.getItem(NATIVE_REFRESH_TOKEN_KEY);
};

const removeNativeRefreshToken = async (): Promise<void> => {
  await prepareSecureStorage();
  await SecureStorage.removeItem(NATIVE_REFRESH_TOKEN_KEY);
};

// Event system for authentication errors
export const AUTH_ERROR_EVENT = 'auth-error';
export const AUTH_SUCCESS_EVENT = 'auth-success';
export const LEGAL_ACCEPTANCE_REQUIRED_EVENT = 'legal-acceptance-required';

export const emitAuthError = () => {
  const event = new CustomEvent(AUTH_ERROR_EVENT);
  window.dispatchEvent(event);
};

const emitAuthSuccess = () => {
  const event = new CustomEvent(AUTH_SUCCESS_EVENT);
  window.dispatchEvent(event);
};

export interface RegisterRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  termsAndConditions: boolean;
  termsVersion: string;
  marketingEmails: boolean;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface GoogleLoginRequest {
  idToken: string;
  /**
   * Only needed when the Google account does not yet resolve to a roade account. The
   * backend answers `google_terms_acceptance_required` when it is missing and required.
   */
  termsVersion?: string;
  marketingEmails?: boolean;
}

export interface ResetPasswordRequest {
  resetKey: string;
  newPassword: string;
  confirmedNewPassword: string;
}

export interface RecoverPasswordRequest {
  email: string;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
  confirmedNewPassword: string;
}

export interface AuthResponse {
  userId: string;
  accessToken: string;
  refreshToken?: string;
  role?: string;
  accountType?: UserAccountType | null;
}

export interface RefreshTokenResponse {
  userId: string | null;
  accessToken: string;
  refreshToken?: string;
  role?: string;
  accountType?: UserAccountType | null;
}

export const register = async (data: RegisterRequest): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/users`, {
    method: 'POST',
    headers: {
      ...getBaseHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    const errorData: BackendErrorResponse = await parseApiErrorResponse(response);

    // Check for duplicate email error (409 status)
    if (response.status === 409 && errorData.errors) {
      const duplicateEmailError = errorData.errors.find(
        (error: any) => error.code === 'user_email_duplicate'
      );
      if (duplicateEmailError) {
        throw new Error('DUPLICATE_EMAIL');
      }
    }

    throw new ApiError(response.status, errorData);
  }

  trackEvent('sign_up', { method: 'email' });
};

export const verifyEmail = async (token: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/users/email/verify`, {
    method: 'POST',
    headers: {
      ...getBaseHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ token })
  });

  if (!response.ok) {
    await throwApiError(response);
  }
};

export const resendVerificationEmail = async (email: string): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/users/email/verification/resend`, {
    method: 'POST',
    headers: {
      ...getBaseHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ email })
  });

  if (!response.ok) {
    await throwApiError(response);
  }
};

export const login = async (data: LoginRequest): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL}/authorise/login-user`, {
    method: 'POST',
    headers: {
      ...getBaseHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data),
    credentials: 'include'
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  const authResponse: AuthResponse = await response.json();

  await saveTokens(authResponse);
  if (!isAdmin()) trackEvent('login', { method: 'password' });

  return authResponse;
};

export const loginWithGoogle = async (data: GoogleLoginRequest): Promise<AuthResponse> => {
  const response = await fetch(`${API_BASE_URL}/authorise/google`, {
    method: 'POST',
    headers: {
      ...getBaseHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data),
    credentials: 'include'
  });

  if (!response.ok) {
    await throwApiError(response);
  }

  const authResponse: AuthResponse = await response.json();

  await saveTokens(authResponse);
  if (!isAdmin()) trackEvent('login', { method: 'google' });

  return authResponse;
};

const performRefresh = async (silent: boolean): Promise<RefreshTokenResponse | null> => {
  try {
    const nativeRefreshToken = isNative ? await getNativeRefreshToken() : null;
    if (isNative && !nativeRefreshToken) {
      clearTokens();
      if (!silent) {
        emitAuthError();
      }
      return null;
    }

    const headers: Record<string, string> = { ...(getBaseHeaders() as Record<string, string>) };
    if (nativeRefreshToken) {
      headers.Authorization = `Bearer ${nativeRefreshToken}`;
    }
    const refresh = () => fetch(`${API_BASE_URL}/authorise/refresh-token`, {
      method: 'POST',
      headers,
      credentials: 'include'
    });
    let response = await refresh();

    // Web Locks are unavailable in some older browsers. Another tab may have
    // rotated the shared cookie while this request was in flight.
    if (!isNative && response.status === 401) {
      await new Promise(resolve => window.setTimeout(resolve, CONCURRENT_REFRESH_RETRY_DELAY_MS));
      response = await refresh();
    }

    if (!response.ok) {
      const authenticationRejected = response.status === 401 || response.status === 403;
      if (isNative && authenticationRejected) {
        await removeNativeRefreshToken().catch(() => undefined);
      }
      if (authenticationRejected) {
        clearTokens();
        if (!silent) {
          emitAuthError();
        }
      }
      return null;
    }

    const data: RefreshTokenResponse = await response.json();

    await saveTokens({
      userId: data.userId || currentUserId || '',
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      role: data.role || currentUserRole || undefined,
      accountType: data.accountType !== undefined ? data.accountType : currentUserAccountType,
    });

    return data;
  } catch (error) {
    // A network failure does not prove that the server-side session ended.
    // Keep the current identity and let the next authenticated operation retry.
    return null;
  }
};

export const refreshAccessToken = async (silent: boolean = false): Promise<RefreshTokenResponse | null> => {
  if (!refreshRequest) {
    const refresh = !isNative && navigator.locks
      ? navigator.locks.request(
          'roade-auth-refresh',
          () => performRefresh(silent),
        ) as unknown as Promise<RefreshTokenResponse | null>
      : performRefresh(silent);
    refreshRequest = refresh.finally(() => {
      refreshRequest = null;
    });
  }
  return refreshRequest;
};

export const saveTokens = async (authResponse: AuthResponse): Promise<void> => {
  accessToken = authResponse.accessToken;
  currentUserId = authResponse.userId;
  currentUserRole = authResponse.role || getRoleFromAccessToken(authResponse.accessToken);
  currentUserAccountType = authResponse.accountType ?? null;
  // This persistent value only avoids cold-start UI flicker. It is never accepted as authentication.
  localStorage.setItem(AUTH_SESSION_HINT_KEY, 'authenticated');
  sessionStorage.setItem(AUTH_SESSION_HINT_KEY, 'authenticated');
  if (isNative && authResponse.refreshToken) {
    await prepareSecureStorage();
    await SecureStorage.setItem(NATIVE_REFRESH_TOKEN_KEY, authResponse.refreshToken);
  }
  emitAuthSuccess();
};

export const getAccessToken = (): string | null => {
  return accessToken;
};

export const getUserId = (): string | null => {
  return currentUserId;
};

export const getUserRole = (): string | null => {
  return currentUserRole;
};

export const getUserAccountType = (): UserAccountType | null => currentUserAccountType;

export const setUserAccountType = (accountType: UserAccountType): void => {
  currentUserAccountType = accountType;
};

export const isAdmin = (): boolean => {
  const role = getUserRole();
  return role === 'ADMIN';
};

const getRoleFromAccessToken = (token: string | null): string | null => {
  if (!token) return null;

  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.role || null;
  } catch (error) {
    return null;
  }
};

export const clearLegacyAuthStorage = (): void => {
  LEGACY_AUTH_STORAGE_KEYS.forEach(key => localStorage.removeItem(key));
};

export const clearTokens = (): void => {
  accessToken = null;
  currentUserId = null;
  currentUserRole = null;
  currentUserAccountType = null;
  localStorage.removeItem(AUTH_SESSION_HINT_KEY);
  sessionStorage.removeItem(AUTH_SESSION_HINT_KEY);
  clearLegacyAuthStorage();
};

export const endLocalSession = (): void => {
  clearTokens();
  emitAuthError();
  if (isNative) {
    void removeNativeRefreshToken().catch(() => undefined);
  }
};

export const hasAuthSessionHint = (): boolean => (
  localStorage.getItem(AUTH_SESSION_HINT_KEY) === 'authenticated'
  || sessionStorage.getItem(AUTH_SESSION_HINT_KEY) === 'authenticated'
);

const getTokenExpirationTime = (token: string): number | null => {
  try {
    const encodedPayload = token.split('.')[1];
    if (!encodedPayload) return null;

    const normalizedPayload = encodedPayload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(encodedPayload.length / 4) * 4, '=');
    const payload = JSON.parse(atob(normalizedPayload));
    return typeof payload.exp === 'number' ? payload.exp * 1000 : null;
  } catch (error) {
    return null;
  }
};

const isTokenExpired = (token: string): boolean => {
  const expirationTime = getTokenExpirationTime(token);
  return expirationTime === null || Date.now() >= expirationTime;
};

const shouldRefreshAccessToken = (token: string): boolean => {
  const expirationTime = getTokenExpirationTime(token);
  return expirationTime === null || Date.now() + ACCESS_TOKEN_REFRESH_SKEW_MS >= expirationTime;
};

export const isAuthenticated = (): boolean => {
  const token = getAccessToken();
  if (!token) {
    return false;
  }
  return !isTokenExpired(token);
};

export const validateAndRefreshTokens = async (silent: boolean = false): Promise<boolean> => {
  if (accessToken && !isTokenExpired(accessToken)) {
    return true;
  }

  const refreshResult = await refreshAccessToken(silent);
  return refreshResult !== null;
};

export const logout = async (): Promise<void> => {
  try {
    const headers: Record<string, string> = { ...(getBaseHeaders() as Record<string, string>) };
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    if (isNative) {
      const nativeRefreshToken = await getNativeRefreshToken();
      if (nativeRefreshToken) {
        headers[NATIVE_REFRESH_TOKEN_HEADER] = nativeRefreshToken;
      }
    }
    await fetch(`${API_BASE_URL}/authorise/logout-user`, {
      method: 'DELETE',
      headers,
      credentials: 'include'
    });
  } catch (error) {
    console.error('Logout API call failed:', error);
  } finally {
    if (isNative) {
      await removeNativeRefreshToken().catch(() => undefined);
    }
    // Without this, Google re-selects the same account silently on the next visit and
    // logging out appears not to have worked.
    forgetGoogleSession();
    clearTokens();
  }
};

export const resetPassword = async (data: ResetPasswordRequest): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/users/password/reset`, {
    method: 'POST',
    headers: {
      ...getBaseHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    await throwApiError(response);
  }
};

export const recoverPassword = async (data: RecoverPasswordRequest): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/users/password/recover`, {
    method: 'POST',
    headers: {
      ...getBaseHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    await throwApiError(response);
  }
};

export const changePassword = async (data: ChangePasswordRequest): Promise<void> => {
  const response = await authenticatedCall(`${API_BASE_URL}/users/password/change`, {
    method: 'POST',
    headers: {
      ...getBaseHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(data)
  });

  if (!response.ok) {
    await throwApiError(response);
  }
};

export const authenticatedCall = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  let accessToken = getAccessToken();

  if (!accessToken || shouldRefreshAccessToken(accessToken)) {
    const refreshResult = await refreshAccessToken();
    if (refreshResult) {
      accessToken = refreshResult.accessToken;
    } else if (!accessToken || isTokenExpired(accessToken)) {
      throw new Error('Not authenticated');
    }
  }

  const headers = {
    ...getBaseHeaders(),
    ...options.headers,
    'Authorization': `Bearer ${accessToken}`
  };

  let response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include'
  });

  if (response.status === 401) {
    const refreshResult = await refreshAccessToken();

    if (!refreshResult) {
      throw new Error('Session expired');
    }

    accessToken = refreshResult.accessToken;

    const retryHeaders = {
      ...headers,
      Authorization: `Bearer ${accessToken}`
    };
    response = await fetch(url, {
      ...options,
      headers: retryHeaders,
      credentials: 'include'
    });

    if (response.status === 401) {
      clearTokens();
      emitAuthError();
      throw new Error('Session expired');
    }
  }

  if (response.status === 428) {
    const errorData = await parseApiErrorResponse(response.clone());
    if (errorData.errors?.some(error => error.code === 'legal_document_acceptance_required')) {
      window.dispatchEvent(new CustomEvent(LEGAL_ACCEPTANCE_REQUIRED_EVENT));
    }
  }

  return response;
};

/**
 * Uses authentication for public endpoints when a browser or native session is
 * being restored, while preserving anonymous access when no session exists.
 * This prevents personalized public fields from being hydrated anonymously
 * during the short cold-start window before the access token is available.
 */
export const optionallyAuthenticatedCall = async (
  url: string,
  options: RequestInit = {}
): Promise<Response> => {
  if (getAccessToken()) {
    return authenticatedCall(url, options);
  }

  if (hasAuthSessionHint()) {
    const refreshResult = await refreshAccessToken(true);
    if (refreshResult) {
      return authenticatedCall(url, options);
    }
  }

  return fetch(url, options);
};
